import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { TrendingUpIcon } from 'lucide-react';
import {
  CardHeader,
  DataTable,
  DatePicker,
  EmptyState,
  ErrorNotice,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
  StatTile,
  TrendChart,
  cn,
  useClientTable,
  type DataTableCard,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  PERMISSION_VIEW_ACCOUNTING,
  PERMISSION_VIEW_SALES_DASHBOARD,
  hasPermission,
  readMyIdentity,
} from '@/lib/events';
import {
  CONTRIBUTION_NOTE,
  EARLY_YEARS_NOTE,
  NO_VALUE,
  completenessText,
  formatMoney,
  formatPerDollar,
  formatPerUnit,
  formatPercent,
  isThin,
  quarterLabel,
  windowText,
} from '@/lib/program-profitability';
import {
  PROFIT_DEFAULT_FROM,
  PROFIT_PARAM,
  PROFIT_TABLE_PREFIX,
  SALES_PROFIT_QUERY_KEY,
  mixNote,
  pointsLabel,
  priceIndexHint,
  priceIndexLabel,
  priceNote,
  readRepProfitability,
  readRepProfitabilityDetail,
  type ProfitWindowQuery,
  type RepProfitRow,
  type RepProfitabilityDetail,
  type RepProgramRow,
} from '@/lib/sales-profitability';
import { FigureCell, MarginCell, MoneyCell } from '@/pages/program-profitability-shared';

/**
 * Profit per rep, on the sales dashboard and the rep page. Shown only to
 * staff holding view_sales_dashboard AND view_accounting; everyone else
 * never sees the section or a hint of it. The API enforces the same pair.
 */

/** Whether the signed-in user may see profit on the sales pages. */
export function useCanSeeSalesProfit(): boolean {
  const identity = useQuery({
    queryKey: ['my-identity'],
    queryFn: ({ signal }) => readMyIdentity(signal),
    staleTime: 5 * 60_000,
  });
  return (
    hasPermission(identity.data, PERMISSION_VIEW_SALES_DASHBOARD) && hasPermission(identity.data, PERMISSION_VIEW_ACCOUNTING)
  );
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The window from the URL (`?profit_from=&profit_to=`), 2023 to today by default. */
function useProfitWindow(): { readonly query: ProfitWindowQuery; readonly setDate: (key: 'from' | 'to', value: string) => void } {
  const [params, setParams] = useSearchParams();
  const query: ProfitWindowQuery = {
    from: params.get(PROFIT_PARAM.from) || PROFIT_DEFAULT_FROM,
    to: params.get(PROFIT_PARAM.to) || today(),
  };
  function setDate(key: 'from' | 'to', value: string): void {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(PROFIT_PARAM[key], value);
        else next.delete(PROFIT_PARAM[key]);
        return next;
      },
      { replace: true },
    );
  }
  return { query, setDate };
}

function ProfitWindowFilters({ query, setDate }: ReturnType<typeof useProfitWindow>): React.ReactElement {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label htmlFor="profit-from" className="mb-1 block text-portal-micro font-bold text-portal-muted">
          Events from
        </label>
        <DatePicker id="profit-from" value={query.from} placeholder="Start" onChange={(value) => setDate('from', value)} />
      </div>
      <div>
        <label htmlFor="profit-to" className="mb-1 block text-portal-micro font-bold text-portal-muted">
          To
        </label>
        <DatePicker id="profit-to" value={query.to} placeholder="End" max={today()} onChange={(value) => setDate('to', value)} />
      </div>
    </div>
  );
}

function ProfitNotes({ minProgramEvents, priceFlagBelow }: { readonly minProgramEvents: number; readonly priceFlagBelow: number }): React.ReactElement {
  return (
    <div className="mt-2 space-y-1 text-portal-micro text-portal-muted">
      <p>Signed events only, cancellations left out. Each event counts for the opportunity owner. {CONTRIBUTION_NOTE}</p>
      <p>{EARLY_YEARS_NOTE}</p>
      <p>{mixNote(minProgramEvents)}</p>
      <p>{priceNote(minProgramEvents, priceFlagBelow)}</p>
    </div>
  );
}

/** "+3.2 pts", green above zero and red below. */
function PointsCell({ value }: { readonly value: number | null }): React.ReactElement {
  return (
    <td
      className={cn(
        'whitespace-nowrap p-3 text-right text-portal-small tabular-nums',
        value === null ? 'text-portal-muted' : value > 0 ? 'text-portal-success' : value < 0 ? 'text-portal-danger' : 'text-portal-ink',
      )}
    >
      {pointsLabel(value)}
    </td>
  );
}

/** A price index, amber when it sits at or past the flag line below the median. */
function PriceIndexCell({ index, flagBelow }: { readonly index: number | null; readonly flagBelow: number }): React.ReactElement {
  const low = index !== null && index <= 100 * (1 - flagBelow);
  return (
    <td className={cn('whitespace-nowrap p-3 text-right text-portal-small tabular-nums', low ? 'text-amber-700 font-bold' : index === null ? 'text-portal-muted' : 'text-portal-ink')}>
      {priceIndexLabel(index)}
    </td>
  );
}

// ─── Dashboard section ───────────────────────────────────────────────────

const REP_PROFIT_SORT_FIELDS = {
  rep: 'ownerName',
  events: 'events',
  revenue: 'revenue',
  contribution: 'contribution',
  margin: 'margin',
  expected: 'expectedMargin',
  vsMix: 'marginVsMix',
  perParticipant: 'contributionPerParticipant',
  perDollar: 'contributionPerRevenueDollar',
  priceIndex: 'priceIndex',
  completeness: 'completeness',
} as const;
const REP_PROFIT_SORT_KEYS = Object.values(REP_PROFIT_SORT_FIELDS);
const REP_PROFIT_DEFAULT_SORT = { key: REP_PROFIT_SORT_FIELDS.contribution, direction: 'desc' } as const;
const REP_PROFIT_COLUMNS = 11;
const REP_PROFIT_RIGHT_ALIGNED = Array.from({ length: REP_PROFIT_COLUMNS - 1 }, (_, i) => i + 1);

function repPath(row: RepProfitRow, query: ProfitWindowQuery): string {
  const params = new URLSearchParams();
  if (query.from !== PROFIT_DEFAULT_FROM) params.set(PROFIT_PARAM.from, query.from);
  if (query.to !== today()) params.set(PROFIT_PARAM.to, query.to);
  const search = params.toString();
  return `/analytics/sales/${row.ownerId}${search ? `?${search}` : ''}`;
}

function RepName({ row, query }: { readonly row: RepProfitRow; readonly query: ProfitWindowQuery }): React.ReactElement {
  return row.ownerId ? (
    <Link to={repPath(row, query)} className="font-bold whitespace-nowrap text-brand hover:underline">
      {row.ownerName}
    </Link>
  ) : (
    <span className="font-bold text-portal-ink">{row.ownerName}</span>
  );
}

/**
 * The Profitability section of Analytics → Sales: one row per rep with
 * contribution, margin against what their program mix would earn, price
 * against the company median, and how much of their data is itemized.
 */
export function SalesProfitabilitySection(): React.ReactElement {
  const window = useProfitWindow();
  const report = useQuery({
    queryKey: SALES_PROFIT_QUERY_KEY.report(window.query),
    queryFn: ({ signal }) => readRepProfitability(window.query, signal),
    placeholderData: keepPreviousData,
  });
  // Same reps as the scorecard above: the graded roster only. Former and other
  // owners still count in the company figures the mix and price index use.
  const rosterReps = useMemo(() => (report.data?.reps ?? []).filter((rep) => rep.onRoster), [report.data]);
  const table = useClientTable<RepProfitRow>({
    rows: rosterReps,
    searchText: (row) => row.ownerName,
    sortKeys: REP_PROFIT_SORT_KEYS,
    defaultSort: REP_PROFIT_DEFAULT_SORT,
    prefix: PROFIT_TABLE_PREFIX,
  });

  const data = report.data;
  const target = data?.targetMargin ?? 0;
  const flagBelow = data?.priceFlagBelow ?? 0;

  const cards: DataTableCard[] = table.pageRows.map((row) => ({
    key: row.ownerId || row.ownerName,
    title: <RepName row={row} query={window.query} />,
    subtitle: `${row.events.toLocaleString('en-US')} events · ${completenessText(row)}`,
    fields: [
      { label: 'Contribution', value: formatMoney(row.contribution) },
      { label: 'Margin', value: formatPercent(row.margin) },
      { label: 'Expected', value: formatPercent(row.expectedMargin) },
      { label: 'vs mix', value: pointsLabel(row.marginVsMix) },
      { label: 'Per participant', value: formatPerUnit(row.contributionPerParticipant) },
      { label: 'Price index', value: priceIndexLabel(row.priceIndex) },
    ],
  }));

  return (
    <section aria-labelledby="sales-profitability" className="mt-8">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="sales-profitability" className="text-portal-card-title font-bold text-portal-ink">
            Profitability
          </h2>
          <p className="text-portal-micro text-portal-muted">
            Event contribution per rep{data ? `, ${windowText(data.window)}` : ''}, against their program mix and the
            company median price.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ProfitWindowFilters {...window} />
          <SearchInput
            label="Search reps by name"
            placeholder="Rep name"
            value={table.search}
            onChange={(event) => table.setSearch(event.target.value)}
            className="w-full max-w-72 min-w-56"
          />
        </div>
      </div>

      {report.isError ? (
        <ErrorNotice title="Profitability unavailable" message={errorMessage(report.error)} />
      ) : !data ? (
        <SkeletonTable rows={6} label="Loading profitability…" />
      ) : rosterReps.length === 0 || table.matched === 0 ? (
        <Panel>
          {rosterReps.length === 0 ? (
            <EmptyState
              icon={<TrendingUpIcon aria-hidden className="size-5" />}
              title="No events in this window"
              message="Reps appear here once signed events in these dates have an income and expense statement. Widen the dates."
            />
          ) : (
            <EmptyState title="No reps match" message="Try a different name." />
          )}
        </Panel>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatTile label="Company contribution" value={formatMoney(data.company.contribution)} hint={`${data.company.events.toLocaleString('en-US')} events`} />
            <StatTile label="Company margin" value={formatPercent(data.company.margin)} hint={`on ${formatMoney(data.company.revenue)} revenue`} />
            <StatTile label="Contribution per participant" value={formatPerUnit(data.company.contributionPerParticipant)} hint={`${data.company.participants.toLocaleString('en-US')} participants`} />
            <StatTile label="Itemized" value={formatPercent(data.company.completeness)} hint="Share of in-person events with costs itemized" />
          </div>
          <DataTable
            headers={[
              { label: 'Rep', sortKey: REP_PROFIT_SORT_FIELDS.rep },
              { label: 'Events', sortKey: REP_PROFIT_SORT_FIELDS.events },
              { label: 'Revenue', sortKey: REP_PROFIT_SORT_FIELDS.revenue },
              { label: 'Contribution', sortKey: REP_PROFIT_SORT_FIELDS.contribution },
              { label: 'Margin', sortKey: REP_PROFIT_SORT_FIELDS.margin },
              { label: 'Expected', sortKey: REP_PROFIT_SORT_FIELDS.expected },
              { label: 'vs mix', sortKey: REP_PROFIT_SORT_FIELDS.vsMix },
              { label: 'Contribution / person', sortKey: REP_PROFIT_SORT_FIELDS.perParticipant },
              { label: 'Per $1 of revenue', sortKey: REP_PROFIT_SORT_FIELDS.perDollar },
              { label: 'Price index', sortKey: REP_PROFIT_SORT_FIELDS.priceIndex },
              { label: 'Itemized', sortKey: REP_PROFIT_SORT_FIELDS.completeness },
            ]}
            rightAlignedHeaders={REP_PROFIT_RIGHT_ALIGNED}
            sort={table.sort}
            onSort={table.onSort}
            minWidth={1320}
            cards={cards}
          >
            {table.pageRows.map((row) => (
              <tr key={row.ownerId || row.ownerName} className="border-t border-portal-line align-middle">
                <td className="min-w-[180px] p-3 text-portal-small">
                  <RepName row={row} query={window.query} />
                </td>
                <FigureCell>{row.events.toLocaleString('en-US')}</FigureCell>
                <MoneyCell value={row.revenue} />
                <MoneyCell value={row.contribution} strong />
                <MarginCell margin={row.margin} target={target} />
                <FigureCell>{formatPercent(row.expectedMargin)}</FigureCell>
                <PointsCell value={row.marginVsMix} />
                <td className="whitespace-nowrap p-3 text-right text-portal-small font-bold tabular-nums text-portal-ink">
                  {formatPerUnit(row.contributionPerParticipant)}
                </td>
                <FigureCell>{formatPerDollar(row.contributionPerRevenueDollar)}</FigureCell>
                <PriceIndexCell index={row.priceIndex} flagBelow={flagBelow} />
                <td className={cn('whitespace-nowrap p-3 text-right text-portal-small tabular-nums', isThin(row) ? 'text-amber-700' : 'text-portal-muted')}>
                  {row.completeness === null ? NO_VALUE : formatPercent(row.completeness)}
                </td>
              </tr>
            ))}
          </DataTable>
          {table.totalPages > 1 ? (
            <Pagination page={table.page} totalPages={table.totalPages} onChange={table.setPage} summary={table.summary} className="mt-3" />
          ) : null}
          <ProfitNotes minProgramEvents={data.minProgramEvents} priceFlagBelow={data.priceFlagBelow} />
        </>
      )}
    </section>
  );
}

// ─── Rep page section ────────────────────────────────────────────────────

const PROGRAM_SORT_FIELDS = {
  program: 'program',
  events: 'events',
  revenue: 'revenue',
  margin: 'margin',
  mixMargin: 'mixMargin',
  price: 'revenuePerParticipant',
  median: 'companyMedianRevenuePerParticipant',
  priceIndex: 'priceIndex',
} as const;
const PROGRAM_SORT_KEYS = Object.values(PROGRAM_SORT_FIELDS);
const PROGRAM_DEFAULT_SORT = { key: PROGRAM_SORT_FIELDS.revenue, direction: 'desc' } as const;
const PROGRAM_COLUMNS = 8;
const PROGRAM_RIGHT_ALIGNED = Array.from({ length: PROGRAM_COLUMNS - 1 }, (_, i) => i + 1);

function MarginTrend({ detail }: { readonly detail: RepProfitabilityDetail }): React.ReactElement {
  const toPoints = (margin: number | null): number => (margin === null ? Number.NaN : Math.round(margin * 1000) / 10);
  return (
    <Panel className="p-5">
      <CardHeader title="Margin by quarter" description="This rep's event margin each quarter, beside the whole company's." />
      <TrendChart
        title="Rep margin by event quarter"
        unit="Margin"
        variant="line"
        points={detail.quarters.map((quarter) => ({
          key: quarter.quarter,
          label: quarterLabel(quarter.quarter),
          value: toPoints(quarter.margin),
          details: [
            `${quarter.events} ${quarter.events === 1 ? 'event' : 'events'}`,
            `${formatMoney(quarter.contribution)} on ${formatMoney(quarter.revenue)}`,
            `Company ${formatPercent(quarter.companyMargin)}`,
          ],
        }))}
        overlays={[{ key: 'company', label: 'Company', values: detail.quarters.map((q) => toPoints(q.companyMargin)), tone: 1 }]}
        formatValue={(value) => `${Math.round(value)}%`}
        height={280}
      />
    </Panel>
  );
}

function ProgramPrices({ detail }: { readonly detail: RepProfitabilityDetail }): React.ReactElement {
  const table = useClientTable<RepProgramRow>({
    rows: detail.programs,
    searchText: (row) => row.program,
    sortKeys: PROGRAM_SORT_KEYS,
    defaultSort: PROGRAM_DEFAULT_SORT,
    prefix: PROFIT_TABLE_PREFIX,
  });
  const flagged = detail.programs.filter((row) => row.flagged).length;
  const flagPct = formatPercent(detail.priceFlagBelow);

  const cards: DataTableCard[] = table.pageRows.map((row) => ({
    key: row.program,
    title: row.program,
    subtitle: `${row.events} ${row.events === 1 ? 'event' : 'events'}${row.flagged ? ` · ${flagPct} or more below the median` : ''}`,
    fields: [
      { label: 'Revenue', value: formatMoney(row.revenue) },
      { label: 'Margin', value: formatPercent(row.margin) },
      { label: 'Company margin', value: formatPercent(row.mixMargin) },
      { label: 'Price per person', value: formatPerUnit(row.revenuePerParticipant) },
      { label: 'Company median', value: formatPerUnit(row.companyMedianRevenuePerParticipant) },
      { label: 'Price index', value: priceIndexLabel(row.priceIndex) },
    ],
  }));

  return (
    <div className="mt-6">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-portal-card-title font-bold text-portal-ink">Price by program</h3>
          <p className="text-portal-micro text-portal-muted">
            Median revenue per participant against the company median for the same program.{' '}
            {flagged > 0 ? `${flagged} ${flagged === 1 ? 'program sits' : 'programs sit'} ${flagPct} or more below it.` : `None sits ${flagPct} or more below it.`}
          </p>
        </div>
        <SearchInput
          label="Search this rep's programs"
          placeholder="Program name"
          value={table.search}
          onChange={(event) => table.setSearch(event.target.value)}
          className="w-full max-w-72 min-w-56"
        />
      </div>
      {table.matched === 0 ? (
        <Panel>
          <EmptyState title="No programs match" message="Try a different name." />
        </Panel>
      ) : (
        <>
          <DataTable
            headers={[
              { label: 'Program', sortKey: PROGRAM_SORT_FIELDS.program },
              { label: 'Events', sortKey: PROGRAM_SORT_FIELDS.events },
              { label: 'Revenue', sortKey: PROGRAM_SORT_FIELDS.revenue },
              { label: 'Margin', sortKey: PROGRAM_SORT_FIELDS.margin },
              { label: 'Company margin', sortKey: PROGRAM_SORT_FIELDS.mixMargin },
              { label: 'Price per person', sortKey: PROGRAM_SORT_FIELDS.price },
              { label: 'Company median', sortKey: PROGRAM_SORT_FIELDS.median },
              { label: 'Price index', sortKey: PROGRAM_SORT_FIELDS.priceIndex },
            ]}
            rightAlignedHeaders={PROGRAM_RIGHT_ALIGNED}
            sort={table.sort}
            onSort={table.onSort}
            minWidth={1080}
            cards={cards}
          >
            {table.pageRows.map((row) => (
              <tr key={row.program} className="border-t border-portal-line align-middle">
                <td className="min-w-[220px] p-3 text-portal-small">
                  <span className="block font-bold text-portal-ink">{row.program}</span>
                  <span className="block text-portal-micro text-portal-muted">
                    {row.flagged ? (
                      <Pill tone="warning">{flagPct} or more below the median</Pill>
                    ) : row.priceComparable ? (
                      `${row.participants.toLocaleString('en-US')} participants`
                    ) : (
                      'Price not compared'
                    )}
                  </span>
                </td>
                <FigureCell>{row.events.toLocaleString('en-US')}</FigureCell>
                <MoneyCell value={row.revenue} />
                <MarginCell margin={row.margin} target={detail.targetMargin} />
                <FigureCell>
                  {formatPercent(row.mixMargin)}
                  {row.usesCompanyMargin ? '*' : ''}
                </FigureCell>
                <FigureCell>{formatPerUnit(row.revenuePerParticipant)}</FigureCell>
                <FigureCell>{row.priceComparable ? formatPerUnit(row.companyMedianRevenuePerParticipant) : NO_VALUE}</FigureCell>
                <PriceIndexCell index={row.priceIndex} flagBelow={detail.priceFlagBelow} />
              </tr>
            ))}
          </DataTable>
          {table.totalPages > 1 ? (
            <Pagination page={table.page} totalPages={table.totalPages} onChange={table.setPage} summary={table.summary} className="mt-3" />
          ) : null}
          <p className="mt-2 text-portal-micro text-portal-muted">
            * Fewer than {detail.minProgramEvents} events company-wide, so the overall company margin stands in.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * The Profitability section of a rep's page: their contribution and margin
 * against their mix, their price index, the quarterly margin beside the
 * company's, and their price on each program against the company median.
 */
export function RepProfitabilitySection({ ownerId }: { readonly ownerId: string }): React.ReactElement {
  const window = useProfitWindow();
  const detail = useQuery({
    queryKey: SALES_PROFIT_QUERY_KEY.rep(ownerId, window.query),
    queryFn: ({ signal }) => readRepProfitabilityDetail(ownerId, window.query, signal),
    placeholderData: keepPreviousData,
  });
  const data = detail.data;

  return (
    <section aria-labelledby="rep-profitability" className="mt-8">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="rep-profitability" className="text-portal-card-title font-bold text-portal-ink">
            Profitability
          </h2>
          <p className="text-portal-micro text-portal-muted">
            Event contribution on the signed events this rep owns{data ? `, ${windowText(data.window)}` : ''}.
          </p>
        </div>
        <ProfitWindowFilters {...window} />
      </div>

      {detail.isError ? (
        <ErrorNotice title="Profitability unavailable" message={errorMessage(detail.error)} />
      ) : !data ? (
        <SkeletonTable rows={4} label="Loading profitability…" />
      ) : data.metrics.events === 0 ? (
        <Panel>
          <EmptyState
            icon={<TrendingUpIcon aria-hidden className="size-5" />}
            title="No events in this window"
            message="This rep's signed events show here once they have an income and expense statement. Widen the dates."
          />
        </Panel>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatTile
              label="Contribution"
              value={formatMoney(data.metrics.contribution)}
              hint={`${data.metrics.events.toLocaleString('en-US')} events on ${formatMoney(data.metrics.revenue)} revenue`}
            />
            <StatTile
              label="Margin"
              value={formatPercent(data.metrics.margin)}
              hint={`${pointsLabel(data.comparisons.marginVsMix)} vs their mix (expected ${formatPercent(data.comparisons.expectedMargin)}) · company ${formatPercent(data.company.margin)}`}
            />
            <StatTile label="Price index" value={priceIndexLabel(data.comparisons.priceIndex)} hint={priceIndexHint(data.comparisons.priceIndex)} />
            <StatTile
              label="Contribution per participant"
              value={formatPerUnit(data.metrics.contributionPerParticipant)}
              hint={`${formatPerDollar(data.metrics.contributionPerRevenueDollar)} per $1 of revenue · ${completenessText(data.metrics)}`}
            />
          </div>
          {data.quarters.length > 1 ? <MarginTrend detail={data} /> : null}
          <ProgramPrices detail={data} />
          <ProfitNotes minProgramEvents={data.minProgramEvents} priceFlagBelow={data.priceFlagBelow} />
        </>
      )}
    </section>
  );
}
